import { Component, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CompanySearchBoxComponent, CompanySearchFieldsConfig } from '../company-search-box/company-search-box.component';
import { CompanySearchFilter } from '../../../models/companySearchFilter';
import { CompanyDto } from '../../../models/api/companyDto';
import { CompanySearchRequest } from '../../../models/api/companySearchRequest';
import { ColumnDefinition } from '../../../models/columnDefinition';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { CompanyService } from '../../../services/companies/company.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Store } from '@ngrx/store';
import { IAppState } from '../../../store/states/app.state';
import { selectCompanyListFullState } from '../../../store/selectors/company-list.selectors';
import { Helpers } from '../../../utilities/helpers';
import { SetCompanySearchFilter, SetCompanyPagination, SetCompanySort } from '../../../store/actions/company-list.actions';
import { filter, take } from 'rxjs/operators';
import { CurrentUserState } from '../../../state/current-user.state';
import { AppUserRole } from '../../../models/enums/appUserRole.enum';
import { MatDialog } from '@angular/material/dialog';
import { ExportColumnsDialogComponent } from './export-columns-dialog/export-columns-dialog.component';

@Component({
  selector: 'app-company-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    CompanySearchBoxComponent,
    GenericListComponent,
    NgxSpinnerModule
  ],
  templateUrl: './company-list.component.html',
  styleUrl: './company-list.component.scss'
})
export class CompanyListComponent {
  @ViewChild(GenericListComponent) genericList?: GenericListComponent<CompanyDto>;
  private currentUserState = inject(CurrentUserState);
  isAdmin = this.currentUserState.user()?.role === AppUserRole.Admin;

  searchFieldsConfig: CompanySearchFieldsConfig = {
    showMeasurement: true,
    showCountry: true,
    showListingStatus: true,
    showIndex: true,
    showName: true,
    showCode: true,
    showIndustry: true,
    showUnitaryDual: true,
    showCapitalization: true,
    showIsEmpty: false,
    showCategory: true,
    showFinished: true,
    showChecked: this.currentUserState.user()?.role !== AppUserRole.User,
    showIsin: true,
    showIsActive: true
  };

  companies: CompanyDto[] = [];
  totalItems = 0;
  currentPage = 0;
  pageSize = 10;
  isSearchVisible = true;
  isExporting = false;

  // Search and sort state
  currentSearchFilter: CompanySearchFilter = { isActive: true, measurement: this.currentUserState.defaultMeasurementYear() };
  private sortBy?: string;
  private ascending = true;

  columnDefinitions: ColumnDefinition[] = [
    { propertyName: 'code', displayName: 'Internal ID' },
    { propertyName: 'matchedYearsTooltip', displayName: '', dataType: 'tooltipIcon', sortable: false },
    { propertyName: 'companyName', displayName: 'Name' },
    { propertyName: 'countryName', displayName: 'Country' },
    { propertyName: 'industryName', displayName: 'Industry' },
    { propertyName: 'stockSymbol', displayName: 'Stock Symbol' },
  ];

  private dialog = inject(MatDialog);

  constructor(
    private router: Router,
    private companyService: CompanyService,
    private confirmationDialog: ConfirmationDialogService,
    private toast: HotToastService,
    private spinnerService: NgxSpinnerService,
    private store: Store<IAppState>
  ) {}

  ngOnInit(): void {
    // Restore state from store
    this.store.select(selectCompanyListFullState).pipe(
      filter(state => state !== undefined && state !== null),
      take(1)
    ).subscribe(state => {
      this.currentSearchFilter = {
        ...state.searchFilter,
        measurement: state.searchFilter.measurement && this.currentUserState.accessibleMeasurements().includes(state.searchFilter.measurement)
          ? state.searchFilter.measurement
          : this.currentUserState.defaultMeasurementYear()
      };
      this.currentPage = state.currentPage - 1; // Convert to 0-based for display
      this.pageSize = state.pageSize;
      this.sortBy = state.sortBy;
      this.ascending = state.ascending;

      this.loadCompanies();
    });
  }

  async onCreateCompany(): Promise<void> {
    try {
      await this.router.navigate(['/companies/create']);
    } catch (error) {
      this.toast.error('Error navigating to Company create (route not found)');
    }
  }

  async onEditCompany(company: CompanyDto): Promise<void> {
    try {
      const queryParams: any = {};
      const year = this.currentSearchFilter.measurement ?? company.year;
      if (year) {
        queryParams.year = year;
      }
      await this.router.navigate(['/companies/edit', company.id], { queryParams });
    } catch (error) {
      this.toast.error('Error navigating to Company edit (route not found)');
    }
  }

  onOpenCompanyInNewTab(company: CompanyDto): void {
    const queryParams: any = {};
    const year = this.currentSearchFilter.measurement ?? company.year;
    if (year) {
      queryParams.year = year;
    }
    const urlTree = this.router.createUrlTree(['/companies/edit', company.id], { queryParams });
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank');
  }

  onDeleteCompany(company: CompanyDto): void {
    this.confirmationDialog.openDeleteConfirmationDialog(company.companyName).subscribe(confirmed => {
      if (!confirmed) return;
      this.spinnerService.show();
      this.companyService.delete(company.id).subscribe({
        next: () => {
          this.toast.success(`"${company.companyName}" deleted successfully`);
          this.loadCompanies();
        },
        error: () => {
          this.toast.error(`Failed to delete "${company.companyName}"`);
          this.spinnerService.hide();
        }
      });
    });
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active;
    this.ascending = sort.direction === 'asc';
    this.currentPage = 0;

    this.store.dispatch(new SetCompanySort({
      sortBy: this.sortBy,
      ascending: this.ascending
    }));

    this.loadCompanies();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;

    this.store.dispatch(new SetCompanyPagination({
      currentPage: this.currentPage + 1, // Store uses 1-based
      pageSize: this.pageSize
    }));

    this.loadCompanies();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onCompanySearchFilterChange(companySearchFilter: CompanySearchFilter): void {
    this.currentSearchFilter = companySearchFilter;
    this.currentPage = 0;

    this.store.dispatch(new SetCompanySearchFilter(companySearchFilter));

    if (this.genericList?.paginator) {
      this.genericList.paginator.pageIndex = 0;
    }

    this.loadCompanies();
  }

  onExportToCsv(): void {
    if (this.isExporting) return;

    this.dialog.open<ExportColumnsDialogComponent, void, string[] | null>(
      ExportColumnsDialogComponent,
      { autoFocus: false, restoreFocus: false, width: '720px', maxWidth: '95vw' }
    ).afterClosed().subscribe(selectedColumnIds => {
      if (!selectedColumnIds || selectedColumnIds.length === 0) return;
      this.runExport(selectedColumnIds);
    });
  }

  private runExport(columns: string[]): void {
    this.isExporting = true;
    this.spinnerService.show();

    const request = { ...this.buildSearchRequest(this.currentPage + 1, this.pageSize), columns };

    this.companyService.exportCompaniesToCsv(request).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `companies_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.isExporting = false;
        this.toast.success('Companies exported successfully');
      },
      error: (error) => {
        console.error('Error exporting companies:', error);
        this.toast.error('Error exporting companies');
        this.spinnerService.hide();
        this.isExporting = false;
      }
    });
  }

  private buildSearchRequest(pageNumber: number, pageSize: number): CompanySearchRequest {
    const listingType = this.currentSearchFilter.listingStatus === 'primary' ? 0
      : this.currentSearchFilter.listingStatus === 'secondary' ? 1
      : this.currentSearchFilter.listingStatus === 'all' ? 2
      : undefined;

    return {
      pageNumber,
      pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending,
      countryId: this.currentSearchFilter.country,
      code: this.currentSearchFilter.code,
      companyName: this.currentSearchFilter.name,
      year: this.currentSearchFilter.measurement ? parseInt(this.currentSearchFilter.measurement, 10) : undefined,
      industryId: this.currentSearchFilter.industry,
      stockIndexId: this.currentSearchFilter.index,
      boardType: this.currentSearchFilter.unitaryDual,
      capitalization: this.currentSearchFilter.capitalization,
      isin: this.currentSearchFilter.isin,
      isActive: this.currentSearchFilter.isActive,
      listingType,
      category: this.currentSearchFilter.category,
      isFinished: this.currentSearchFilter.finished === 'yes' ? true
        : this.currentSearchFilter.finished === 'no' ? false
        : undefined,
      isChecked: this.currentSearchFilter.checked === 'yes' ? true
        : this.currentSearchFilter.checked === 'no' ? false
        : undefined
    };
  }

  private loadCompanies(): void {
    this.spinnerService.show();

    const searchRequest = this.buildSearchRequest(this.currentPage + 1, this.pageSize);

    this.companyService.search(searchRequest).subscribe({
      next: (response) => {
        this.companies = response.data.map(c => {
          if (c.matchedYears?.length && !c.matchedYears.includes(c.year)) {
            return { ...c, matchedYearsTooltip: `Matched in ${c.matchedYears.join(', ')}` } as CompanyDto;
          }
          return c;
        });
        this.totalItems = response.totalCount;
        this.currentPage = response.page - 1; // Convert back to 0-based for display
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.toast.error('Failed to load companies');
        this.spinnerService.hide();
      }
    });
  }
}
